import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { useProfile } from '../hooks/useProfile';

interface AccessRecord {
  id: string;
  allocator_firm_id: string;
  status: string;
  requested_at: string;
  granted_at: string | null;
  allocator_firm_name: string;
}

interface Question {
  id: string;
  allocator_firm_id: string;
  section_key: string;
  question_text: string;
  answer_text: string | null;
  asked_at: string;
  answered_at: string | null;
  allocator_firm_name: string;
}

interface DDQ {
  id: string;
  type: string;
  asset_class: string;
  status: string;
  allow_pdf: boolean;
  fund_id: string | null;
}

export default function DDQAccess() {
  const { ddqId } = useParams();
  const navigate = useNavigate();
  const { firm } = useProfile();
  const [ddq, setDdq] = useState<DDQ | null>(null);
  const [accessRecords, setAccessRecords] = useState<AccessRecord[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'access' | 'questions'>('access');
  const [answeringId, setAnsweringId] = useState<string | null>(null);
  const [answerText, setAnswerText] = useState('');
  const [submittingAnswer, setSubmittingAnswer] = useState(false);
  const [selectedAllocatorId, setSelectedAllocatorId] = useState('');
  const [sharing, setSharing] = useState(false);
  const [shareSuccess, setShareSuccess] = useState(false);
  const [connectedAllocators, setConnectedAllocators] = useState<{ id: string; name: string }[]>([]);

  useEffect(() => {
    if (!ddqId) return;
    fetchData();
  }, [ddqId, firm]);

  const fetchData = async () => {
    const { data: ddqData } = await supabase
      .from('ddqs').select('*').eq('id', ddqId).single();
    if (ddqData) setDdq(ddqData);

    const { data: accessData } = await supabase
      .from('ddq_access').select('*').eq('ddq_id', ddqId)
      .order('requested_at', { ascending: false });

    if (accessData) {
      const enriched = await Promise.all(accessData.map(async (a) => {
        const { data: firmData } = await supabase
          .from('firms').select('name').eq('id', a.allocator_firm_id).single();
        return { ...a, allocator_firm_name: firmData?.name || 'Unknown' };
      }));
      setAccessRecords(enriched);
    }

    const { data: questionData } = await supabase
      .from('ddq_questions').select('*').eq('ddq_id', ddqId)
      .order('asked_at', { ascending: false });

    if (questionData) {
      const enriched = await Promise.all(questionData.map(async (q) => {
        const { data: firmData } = await supabase
          .from('firms').select('name').eq('id', q.allocator_firm_id).single();
        return { ...q, allocator_firm_name: firmData?.name || 'Unknown' };
      }));
      setQuestions(enriched);
    }

    // Fetch connected allocators
    if (firm) {
      const { data: connections } = await supabase
        .from('connections')
        .select('allocator_firm_id')
        .eq('manager_firm_id', firm.id)
        .eq('status', 'approved');

      if (connections && connections.length > 0) {
        const allocFirmIds = connections.map(c => c.allocator_firm_id);
        const { data: allocFirms } = await supabase
          .from('firms')
          .select('id, name')
          .in('id', allocFirmIds);
        if (allocFirms) setConnectedAllocators(allocFirms);
      }
    }

    setLoading(false);
  };

  const handleAccessAction = async (id: string, action: 'granted' | 'declined' | 'revoked') => {
    await supabase.from('ddq_access').update({
      status: action,
      ...(action === 'granted' ? { granted_at: new Date().toISOString() } : {}),
    }).eq('id', id);
    fetchData();
  };

  const handleAnswerQuestion = async (questionId: string) => {
    if (!answerText.trim()) return;
    setSubmittingAnswer(true);
    await supabase.from('ddq_questions').update({
      answer_text: answerText,
      answered_at: new Date().toISOString(),
    }).eq('id', questionId);
    setAnsweringId(null);
    setAnswerText('');
    setSubmittingAnswer(false);
    fetchData();
  };

  const handleShareWithAllocator = async () => {
    if (!selectedAllocatorId.trim() || !ddqId || !firm) return;
    setSharing(true);

    const { error } = await supabase.from('ddq_access').upsert({
      ddq_id: ddqId,
      allocator_firm_id: selectedAllocatorId,
      status: 'granted',
      granted_at: new Date().toISOString(),
    }, { onConflict: 'ddq_id,allocator_firm_id' });

    if (!error) {
      setShareSuccess(true);
      setSelectedAllocatorId('');
      setTimeout(() => setShareSuccess(false), 3000);
      fetchData();
    }
    setSharing(false);
  };

  const handleTogglePdf = async () => {
    if (!ddq) return;
    await supabase.from('ddqs').update({ allow_pdf: !ddq.allow_pdf }).eq('id', ddqId);
    setDdq(prev => prev ? { ...prev, allow_pdf: !prev.allow_pdf } : null);
  };

  const statusColors: Record<string, { color: string; bg: string }> = {
    granted: { color: '#0A7C42', bg: 'rgba(10,124,66,0.1)' },
    requested: { color: '#92640A', bg: 'rgba(146,100,10,0.1)' },
    declined: { color: '#C0222A', bg: 'rgba(192,34,42,0.1)' },
    revoked: { color: '#6B7280', bg: 'rgba(107,114,128,0.1)' },
  };

  if (loading) return (
    <div style={{ padding: '28px', background: '#F5F6F8', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: '#6B7280' }}>Loading...</div>
    </div>
  );

  const unansweredCount = questions.filter(q => !q.answered_at).length;

  return (
    <div style={{ padding: '28px', background: '#F5F6F8', minHeight: '100vh' }}>
      <button onClick={() => navigate('/manage-ddq')} style={{
        background: 'transparent', border: '1px solid #D0D5DD',
        borderRadius: '4px', color: '#6B7280', padding: '6px 12px',
        cursor: 'pointer', fontSize: '12px', marginBottom: '24px',
        fontFamily: 'system-ui, -apple-system, sans-serif'
      }}>← Back to DDQ Manager</button>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontFamily: 'Georgia, serif', fontWeight: 400, fontSize: '24px', color: '#0A0A0A', marginBottom: '4px' }}>
            DDQ Access & Q&A
          </h1>
          <p style={{ fontSize: '13px', color: '#6B7280' }}>
            {ddq?.type === 'firm' ? 'Firm-level DDQ' : 'Fund-level DDQ'} · {accessRecords.filter(r => r.status === 'granted').length} allocators with access
          </p>
        </div>

        {/* PDF toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#FFFFFF', border: '1px solid #D0D5DD', borderRadius: '4px', padding: '10px 16px' }}>
          <div>
            <div style={{ fontSize: '12px', color: '#0A0A0A' }}>PDF download</div>
            <div style={{ fontSize: '10px', color: '#6B7280' }}>Allow allocators to download</div>
          </div>
          <button onClick={handleTogglePdf} style={{
            width: '44px', height: '24px', borderRadius: '12px',
            background: ddq?.allow_pdf ? '#0A1628' : '#D0D5DD',
            border: 'none', cursor: 'pointer', position: 'relative', transition: 'background 0.2s'
          }}>
            <div style={{
              width: '18px', height: '18px', borderRadius: '50%', background: '#FFFFFF',
              position: 'absolute', top: '3px',
              left: ddq?.allow_pdf ? '23px' : '3px', transition: 'left 0.2s'
            }} />
          </button>
        </div>
      </div>

      {/* Share with allocator */}
      <div style={{ background: '#FFFFFF', border: '1px solid #D0D5DD', borderRadius: '4px', padding: '18px 20px', marginBottom: '20px' }}>
        <div style={{ fontSize: '13px', fontWeight: 500, color: '#0A0A0A', marginBottom: '10px' }}>
          Share with connected allocator
        </div>
        {connectedAllocators.length === 0 ? (
          <div style={{ fontSize: '12px', color: '#6B7280' }}>No connected allocators yet</div>
        ) : (
          <div style={{ display: 'flex', gap: '10px' }}>
            <select
              value={selectedAllocatorId}
              onChange={e => setSelectedAllocatorId(e.target.value)}
              style={{
                flex: 1, background: '#FFFFFF', border: '1px solid #D0D5DD',
                borderRadius: '3px', color: '#0A0A0A', fontSize: '13px', padding: '9px 14px',
                outline: 'none', fontFamily: 'system-ui, -apple-system, sans-serif'
              }}
            >
              <option value="">Select allocator...</option>
              {connectedAllocators.map(a => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
            <button
              onClick={handleShareWithAllocator}
              disabled={!selectedAllocatorId.trim() || sharing}
              style={{
                background: shareSuccess ? '#0A7C42' : '#0A1628',
                border: 'none',
                borderRadius: '3px', color: '#FFFFFF', fontSize: '13px',
                padding: '9px 18px', cursor: 'pointer', fontFamily: 'system-ui, -apple-system, sans-serif',
                whiteSpace: 'nowrap'
              }}
            >{shareSuccess ? '✓ Shared' : sharing ? 'Sharing...' : 'Grant access'}</button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '2px', background: '#FFFFFF', border: '1px solid #D0D5DD', borderRadius: '4px', padding: '4px', width: 'fit-content', marginBottom: '20px' }}>
        {[
          { key: 'access', label: `Access (${accessRecords.length})` },
          { key: 'questions', label: `Questions${unansweredCount > 0 ? ` (${unansweredCount} unanswered)` : ''}` },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key as any)} style={{
            padding: '6px 16px', borderRadius: '3px', fontSize: '13px',
            cursor: 'pointer', border: 'none',
            background: activeTab === tab.key ? '#0A1628' : 'transparent',
            color: activeTab === tab.key ? '#FFFFFF' : '#6B7280',
            fontFamily: 'system-ui, -apple-system, sans-serif',
          }}>{tab.label}</button>
        ))}
      </div>

      {activeTab === 'access' && (
        <>
          {accessRecords.length === 0 ? (
            <div style={{ background: '#FFFFFF', border: '1px solid #D0D5DD', borderRadius: '4px', padding: '40px', textAlign: 'center' }}>
              <div style={{ fontSize: '13px', color: '#6B7280' }}>No access records yet, share with allocators above</div>
            </div>
          ) : (
            accessRecords.map(record => {
              const sc = statusColors[record.status] || statusColors.declined;
              return (
                <div key={record.id} style={{ background: '#FFFFFF', border: '1px solid #D0D5DD', borderRadius: '4px', padding: '16px 20px', marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 500, color: '#0A0A0A', marginBottom: '4px' }}>
                      {record.allocator_firm_name}
                    </div>
                    <div style={{ fontSize: '11px', color: '#6B7280' }}>
                      Requested {new Date(record.requested_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      {record.granted_at && ` · Granted ${new Date(record.granted_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '11px', padding: '2px 10px', borderRadius: '20px', background: sc.bg, color: sc.color, textTransform: 'capitalize' }}>
                      {record.status}
                    </span>
                    {record.status === 'requested' && (
                      <>
                        <button onClick={() => handleAccessAction(record.id, 'declined')} style={{ background: '#FFFFFF', border: '1px solid #D0D5DD', borderRadius: '3px', color: '#C0222A', fontSize: '11px', padding: '4px 10px', cursor: 'pointer', fontFamily: 'system-ui, -apple-system, sans-serif' }}>Decline</button>
                        <button onClick={() => handleAccessAction(record.id, 'granted')} style={{ background: '#0A7C42', border: 'none', borderRadius: '3px', color: '#FFFFFF', fontSize: '11px', padding: '4px 10px', cursor: 'pointer', fontFamily: 'system-ui, -apple-system, sans-serif' }}>Grant</button>
                      </>
                    )}
                    {record.status === 'granted' && (
                      <button onClick={() => handleAccessAction(record.id, 'revoked')} style={{ background: '#FFFFFF', border: '1px solid #D0D5DD', borderRadius: '3px', color: '#6B7280', fontSize: '11px', padding: '4px 10px', cursor: 'pointer', fontFamily: 'system-ui, -apple-system, sans-serif' }}>Revoke</button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </>
      )}

      {activeTab === 'questions' && (
        <>
          {questions.length === 0 ? (
            <div style={{ background: '#FFFFFF', border: '1px solid #D0D5DD', borderRadius: '4px', padding: '40px', textAlign: 'center' }}>
              <div style={{ fontSize: '13px', color: '#6B7280' }}>No questions yet from allocators</div>
            </div>
          ) : (
            questions.map(q => (
              <div key={q.id} style={{ background: '#FFFFFF', border: `1px solid ${!q.answered_at ? 'rgba(146,100,10,0.3)' : '#D0D5DD'}`, borderRadius: '4px', padding: '18px 20px', marginBottom: '10px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                  <div>
                    <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>
                      {q.allocator_firm_name} · {q.section_key?.replace(/_/g, ' ')} · {new Date(q.asked_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                    </div>
                    <div style={{ fontSize: '14px', color: '#0A0A0A' }}>{q.question_text}</div>
                  </div>
                  {!q.answered_at && (
                    <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '20px', background: 'rgba(146,100,10,0.1)', color: '#92640A', flexShrink: 0, marginLeft: '12px' }}>
                      Unanswered
                    </span>
                  )}
                </div>

                {q.answer_text && (
                  <div style={{ background: '#F5F6F8', borderRadius: '4px', padding: '12px 14px', marginBottom: '10px' }}>
                    <div style={{ fontSize: '11px', color: '#6B7280', marginBottom: '4px' }}>Your answer</div>
                    <div style={{ fontSize: '13px', color: '#0A0A0A', lineHeight: 1.6 }}>{q.answer_text}</div>
                  </div>
                )}

                {!q.answered_at && answeringId !== q.id && (
                  <button
                    onClick={() => { setAnsweringId(q.id); setAnswerText(''); }}
                    style={{ background: '#0A1628', border: 'none', borderRadius: '3px', color: '#FFFFFF', fontSize: '12px', padding: '6px 14px', cursor: 'pointer', fontFamily: 'system-ui, -apple-system, sans-serif' }}
                  >Answer</button>
                )}

                {answeringId === q.id && (
                  <div>
                    <textarea
                      value={answerText}
                      onChange={e => setAnswerText(e.target.value)}
                      placeholder="Type your answer..."
                      rows={3}
                      style={{ width: '100%', background: '#FFFFFF', border: '1px solid #D0D5DD', borderRadius: '3px', color: '#0A0A0A', fontSize: '13px', padding: '10px 14px', outline: 'none', fontFamily: 'system-ui, -apple-system, sans-serif', resize: 'vertical', boxSizing: 'border-box' as const, lineHeight: 1.6 }}
                    />
                    <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                      <button onClick={() => setAnsweringId(null)} style={{ background: '#FFFFFF', border: '1px solid #D0D5DD', borderRadius: '3px', color: '#3D4552', fontSize: '12px', padding: '7px 14px', cursor: 'pointer', fontFamily: 'system-ui, -apple-system, sans-serif' }}>Cancel</button>
                      <button onClick={() => handleAnswerQuestion(q.id)} disabled={!answerText.trim() || submittingAnswer} style={{ background: '#0A1628', border: 'none', borderRadius: '3px', color: '#FFFFFF', fontSize: '12px', fontWeight: 600, padding: '7px 18px', cursor: 'pointer', fontFamily: 'system-ui, -apple-system, sans-serif' }}>Submit answer</button>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </>
      )}
    </div>
  );
}
